import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ConfirmationDialog from '../../ConfirmationDialog';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { Passenger } from '../../../data/models/passenger.model';
import * as passengerSlice from '../../../data/passenger.slice';
import { useAddress } from '../../../hooks/display/useAddress';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'PassengerNamedLocationsList';

const classes = {
  container: `${PREFIX}-container`,
  namedLocation: `${PREFIX}-namedLocation`,
  namedLocationTitle: `${PREFIX}-namedLocationTitle`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    gap: theme.spacing(2),
  },

  [`& .${classes.namedLocation}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'end',
    borderRadius: 4,
  },
  [`& .${classes.namedLocationTitle}`]: {
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface NamedLocationsListProps {
  passenger: Passenger | null;
}

export default function PassengerNamedLocationsList({
  passenger,
}: NamedLocationsListProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [deleteAddressId, setDeleteAddressId] = React.useState(0);
  const locations = passenger?.passengerAddresses;
  const passengerUUID = passenger?.passengerUUID || '';
  const isSuperReaderUser = useIsSuperReaderUser();

  const confirmDialogPostiveFn = () => {
    const label = passenger?.passengerAddresses[deleteAddressId].label || '';
    dispatch(
      passengerSlice.deletePassengerReservationAddressAsync(
        passengerUUID,
        label,
      ),
    );

    setConfirmDialogOpen(false);
  };

  const confirmDialogNegativeFn = () => {
    setConfirmDialogOpen(false);
  };

  const handleDelete = (index: number) => {
    setConfirmDialogOpen(true);
    setDeleteAddressId(index);
  };

  return (
    <>
      <StyledBox className={classes.container}>
        {locations &&
          locations.map((location, index) => (
            <Box
              key={index}
              className={classes.namedLocation}
              data-testid="passenger-named-location-row">
              <Box className={classes.namedLocationTitle}>
                <Typography
                  variant="subtitle2"
                  data-testid="passenger-named-location-value">
                  {useAddress(location)}
                </Typography>
                <Typography
                  variant="body2"
                  data-testid="passenger-named-location-label">
                  {location.label}
                </Typography>
              </Box>
              {location.passengerAddressUseBC !== 'ELIGIBILITYFILE' &&
                !isSuperReaderUser && (
                  <Box>
                    <Tooltip title={t('passenger.deleteNamedLocation')}>
                      <span>
                        <IconButton
                          onClick={() => handleDelete(index)}
                          color="error"
                          size="small"
                          data-testid="passenger-named-location-delete-button">
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                )}
            </Box>
          ))}
      </StyledBox>
      <ConfirmationDialog
        title={t('passenger.deleteNamedLocation')}
        message={t('passenger.confirmDeleteNamedLocation')}
        open={confirmDialogOpen}
        positiveAction={confirmDialogPostiveFn}
        negativeAction={confirmDialogNegativeFn}
      />
    </>
  );
}
